import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { type EmbeddedWebSdk, Fourthline, type Locale } from '@repo/kyc';
import { useSegment } from '@repo/poly-hooks/segment';
import { useSentry } from '@repo/shared-kit/sentry';
import { KycProcessStatus, KycProvider, kycQueries } from '@repo/api-kit/queries/due-diligence/kyc';
import { useKycContext } from '../context';
import { useFourthlineProcess } from './use-fourthline-process';
import { KycError } from '../error';
import { FourthlineLoading } from '../loading';
import { getFourthlineProviderDetails, isKycExpired } from '../utils';

export function FourthlineProvider() {
  const segment = useSegment();
  const { locale } = useIntl();
  const sentry = useSentry();

  const { legalCountry, trackingPrefix, membershipId, onBack, onComplete, isRegisterFlow } =
    useKycContext();
  const market = isRegisterFlow ? legalCountry : undefined;
  const { data: kycInfo, refetch } = useSuspenseQuery(kycQueries.item({ market }));
  const cancelled = useRef(false);

  const providerDetails = getFourthlineProviderDetails(kycInfo);
  const processStatus = kycInfo.activeProcess?.processStatus;

  const { token, redirectUrl, legacyProcess, isLoading } = useFourthlineProcess(providerDetails);

  useEffect(() => {
    return () => {
      cancelled.current = true;
    };
  }, []);

  useEffect(() => {
    if (processStatus === KycProcessStatus.submitted && !cancelled.current) {
      onComplete(KycProvider.fourthline);
    }
  }, [processStatus, onComplete]);

  const handleOnboardingStarted = useCallback(() => {
    segment.track(`${trackingPrefix}_fourthline_verification_screen_loaded`, {
      domain: window.location.hostname,
    });
  }, [segment, trackingPrefix]);

  const handleOnboardingCompleted = useCallback(() => {
    cancelled.current = true;

    onComplete(KycProvider.fourthline);
  }, [onComplete]);

  const handleOnboardingCompletedError = useCallback(() => {
    // The error screen is handled by the WebSDK
    segment.track(`${trackingPrefix}_fourthline_error_screen_loaded`, {
      domain: window.location.hostname,
    });
  }, [segment, trackingPrefix]);

  const handleOnboardingRestartRequest = useCallback(
    async (webSdkElement: { setToken: (token: string) => void }) => {
      segment.track(`${trackingPrefix}_fourthline_regenerate_link_clicked`, {
        domain: window.location.hostname,
      });

      const { data } = await refetch();
      const refreshedToken = data?.activeProcess?.providerDetails?.value.token;

      if (refreshedToken) {
        webSdkElement.setToken(refreshedToken);
      }
    },
    [segment, trackingPrefix, refetch]
  );

  const embeddedWebSdkProps = useMemo(
    () =>
      ({
        // oxlint-disable-next-line typescript/consistent-type-assertions
        locale: locale as Locale,
        token,
        events: {
          desktop: {
            onOnboardingStarted: handleOnboardingStarted,
            onOnboardingCompleted: handleOnboardingCompleted,
            onOnboardingCompletedError: handleOnboardingCompletedError,
            onOnboardingRestartRequest: handleOnboardingRestartRequest,
          },
        },
      }) satisfies EmbeddedWebSdk,
    [
      locale,
      token,
      handleOnboardingStarted,
      handleOnboardingCompleted,
      handleOnboardingCompletedError,
      handleOnboardingRestartRequest,
    ]
  );

  if (processStatus === KycProcessStatus.submitted) return null;

  if (isLoading) {
    return <FourthlineLoading />;
  }

  if (!token || !redirectUrl) {
    sentry.captureException(new Error('Fourthline KYC credentials missing'), {
      extra: {
        kycInfo,
        membershipId,
        providerDetails: providerDetails ?? null,
        legacyProcess: legacyProcess ?? null,
      },
      tags: {
        component: 'FourthlineProvider',
        provider: 'fourthline',
      },
    });

    return <KycError onRetry={refetch} />;
  }

  const isExpired = isKycExpired(kycInfo);
  const isQesRequired = Boolean(kycInfo.activeProcess?.signatureRequired);

  return (
    <Fourthline
      embeddedWebSdkProps={embeddedWebSdkProps}
      fourthlineRedirectUrl={redirectUrl}
      isKycExpired={isExpired}
      isKycRequired
      isQesRequired={isQesRequired}
      legalCountry={legalCountry}
      mode={trackingPrefix}
      onBack={onBack}
    />
  );
}
